import React, { useReducer, useContext, type ReactNode } from 'react';
import axios from 'axios';

import { AuthState } from 'services/auth';
import { API_URL } from 'consts';
import type {
  PreparedWorkPayload,
  QaDayResponse,
  QaReportPayload,
  UserListItem,
  WorkEntryPayload,
  WorkFormPayload,
} from 'types/api';

interface WorkStateShape {
  loading: boolean | null;
  error: unknown;
  data: WorkEntryPayload[] | null;
  daily: WorkEntryPayload[] | null;
  qaWork: QaDayResponse | null;
  qaReport: QaReportPayload | null;
  unfiled: UserListItem[] | null;
}

interface DailyWorkArgs {
  date: string;
  username?: string;
}

interface QaDailyWorkArgs {
  date: string;
}

interface QaReportArgs {
  date: string;
  userId: string;
}

interface UnfiledWorkArgs {
  date: string;
}

type UpdateJobArgs = Record<string, unknown> & { jobId?: string };

interface WorkContextValue extends WorkStateShape {
  getDailyWork: (args: DailyWorkArgs) => void;
  getQaDailyWork: (args: QaDailyWorkArgs) => void;
  getQaReport: (args: QaReportArgs) => void;
  getUnfiledWork: (args: UnfiledWorkArgs) => void;
  updateJob: (args: UpdateJobArgs) => Promise<void>;
}

type WorkAction =
  | { type: 'FETCH_WORK_START' }
  | { type: 'FETCH_QA_START' }
  | { type: 'UPDATE_JOB_START' }
  | { type: 'FETCH_WORK_SUCCESS'; work: WorkEntryPayload[] }
  | { type: 'FETCH_QAREPORT_SUCCESS'; qaReport: QaReportPayload }
  | { type: 'FETCH_DAY_SUCCESS'; daily: WorkEntryPayload[] }
  | { type: 'FETCH_QA_SUCCESS'; qaWork: QaDayResponse }
  | { type: 'FETCH_UNFILED_SUCCESS'; payload: UserListItem[] }
  | { type: 'UPDATE_JOB_SUCCESS' }
  | { type: 'FETCH_WORK_FAIL'; error: unknown }
  | { type: 'FETCH_QA_FAIL'; error: unknown }
  | { type: 'FETCH_QAREPORT_FAIL'; error: unknown }
  | { type: 'UPDATE_JOB_FAIL'; error?: unknown };

const initialState: WorkStateShape = {
  loading: null,
  error: null,
  data: null,
  daily: null,
  qaWork: null,
  qaReport: null,
  unfiled: null,
};

const noopAsync = async () => {};

export const WorkState = React.createContext<WorkContextValue>({
  ...initialState,
  getDailyWork: () => {},
  getQaDailyWork: () => {},
  getQaReport: () => {},
  getUnfiledWork: () => {},
  updateJob: noopAsync,
});

const reducer = (state: WorkStateShape, action: WorkAction): WorkStateShape => {
  switch (action.type) {
    case 'FETCH_WORK_START':
    case 'FETCH_QA_START':
    case 'UPDATE_JOB_START':
      return { ...state, loading: true, error: null };
    case 'FETCH_WORK_SUCCESS':
      return { ...state, loading: false, data: action.work };
    case 'FETCH_QAREPORT_SUCCESS':
      return { ...state, qaReport: action.qaReport };
    case 'FETCH_DAY_SUCCESS':
      return { ...state, loading: false, daily: action.daily };
    case 'FETCH_QA_SUCCESS':
      return { ...state, loading: false, qaWork: action.qaWork };
    case 'FETCH_UNFILED_SUCCESS':
      return { ...state, loading: false, unfiled: action.payload };
    case 'UPDATE_JOB_SUCCESS':
      return { ...state, loading: false };
    case 'FETCH_WORK_FAIL':
    case 'FETCH_QA_FAIL':
    case 'FETCH_QAREPORT_FAIL':
    case 'UPDATE_JOB_FAIL':
      return { ...state, loading: false, error: action.error || null };
    default:
      return state;
  }
};

export const WorkProvider = ({ children }: { children: ReactNode }) => {
  const { access_token } = useContext(AuthState);
  const [state, dispatch] = useReducer(reducer, initialState);

  const getDailyWork = ({ date, username }: DailyWorkArgs) => {
    const uri = username ? `work/${date}/${username}` : `work/${date}`;

    void axios({
      method: 'GET',
      url: `${API_URL}/${uri}`,
      headers: {
        authorization: `Bearer: ${access_token}`,
      },
    })
      .then((res) => {
        const payload = res.data as WorkEntryPayload[];

        if (username) {
          dispatch({ type: 'FETCH_WORK_SUCCESS', work: payload });
        } else {
          dispatch({ type: 'FETCH_DAY_SUCCESS', daily: payload });
        }
      })
      .catch((err) => {
        dispatch({ type: 'FETCH_WORK_FAIL', error: err });
      });
  };

  const getQaDailyWork = ({ date }: QaDailyWorkArgs) => {
    void axios({
      method: 'GET',
      url: `${API_URL}/qa/${date}`,
      headers: {
        authorization: `Bearer: ${access_token}`,
      },
    })
      .then((res) => {
        dispatch({ type: 'FETCH_QA_SUCCESS', qaWork: res.data as QaDayResponse });
      })
      .catch((err) => {
        dispatch({ type: 'FETCH_QA_FAIL', error: err });
      });
  };

  const getQaReport = ({ date, userId }: QaReportArgs) => {
    void axios({
      method: 'GET',
      url: `${API_URL}/work/${date}/${userId}/qareport`,
      headers: {
        authorization: `Bearer: ${access_token}`,
      },
    })
      .then((res) => {
        dispatch({ type: 'FETCH_QAREPORT_SUCCESS', qaReport: res.data as QaReportPayload });
      })
      .catch((err) => {
        dispatch({ type: 'FETCH_QAREPORT_FAIL', error: err });
      });
  };

  const getUnfiledWork = ({ date }: UnfiledWorkArgs) => {
    const uri = `work/${date}/unfiled`;

    void axios({
      method: 'GET',
      url: `${API_URL}/${uri}`,
      headers: {
        authorization: `Bearer: ${access_token}`,
      },
    })
      .then((res) => {
        dispatch({ type: 'FETCH_UNFILED_SUCCESS', payload: res.data as UserListItem[] });
      })
      .catch((err) => {
        dispatch({ type: 'FETCH_WORK_FAIL', error: err });
      });
  };

  const updateJob = async ({ jobId, ...rest }: UpdateJobArgs) => {
    dispatch({ type: 'UPDATE_JOB_START' });
    const uri = jobId ? `work/${jobId}` : 'work';

    try {
      await axios({
        method: 'POST',
        url: `${API_URL}/${uri}`,
        data: rest,
        headers: {
          authorization: `Bearer: ${access_token}`,
        },
      });

      dispatch({ type: 'UPDATE_JOB_SUCCESS' });
    } catch (err) {
      dispatch({ type: 'UPDATE_JOB_FAIL', error: err });
    }
  };

  const value: WorkContextValue = {
    ...state,
    getDailyWork,
    getQaDailyWork,
    getQaReport,
    getUnfiledWork,
    updateJob,
  };

  return <WorkState.Provider value={value}>{children}</WorkState.Provider>;
};

export const preparePayload = (work: WorkFormPayload): PreparedWorkPayload => {
  const payload: PreparedWorkPayload = {
    norme: [],
    otpremnice: [],
    rezije: [],
    napomena: work.napomena || '',
  };

  for (let index = 0; index < work.entries.length; index++) {
    const el = work.entries[index];

    if (el.entry) {
      if (el.type === 'norma') {
        payload.norme.push({
          id: el.entry.id,
          value: el.entry.workAmount,
        });
      } else if (el.type === 'otpremnica') {
        payload.otpremnice.push({
          id: el.entry.id,
          value: el.entry.workAmount,
        });
      } else if (el.type === 'rezija') {
        payload.rezije.push({
          id: el.entry.id,
          value: el.entry.workAmount,
        });
      }
    }
  }

  return payload;
};
